import {
    isRegionId,
    isVariantId,
    normalizeVariantId,
    parseRegionId,
    isRsId,
} from '@gnomad/identifiers'

import { isStructuralVariantId } from './identifiers'
import { ApolloClient } from '@apollo/client'
import { Dispatch } from 'react'
import { TOGGLE_SEARCH_ERROR } from '@/store/types'
import { REGION_LENGTH_QUERY } from '@/graphql/query/regionCount'
import {SearchSuggestions} from '@/generated/graphql'
import {SEARCH_SUGGESTIONS_QUERY} from '@/graphql/query/queries'

export const fetchSearchResults = (
    query: string,
    apolloClient: ApolloClient<any>,
    dispatch: Dispatch<any>,
    datasetId: string
) => {
    if(datasetId === 'placeholder') {
        console.error('No dataset has been selected!')
        return Promise.resolve([
            {
                label: 'No dataset selected',
                value: '/no-dataset'
            }
        ])
    }

    // ==============================================================================================
    // Try to match region first
    // ==============================================================================================
    if (isRegionId(query)) {
        const { chrom, start, stop } = parseRegionId(query)
        const regionId = `${chrom}-${start}-${stop}`

        return apolloClient
            .query<{ regionCount: number }>({
                query: REGION_LENGTH_QUERY,
                variables: {
                    chrom,
                    start,
                    stop,
                    datasetId
                },
            })
            .then(data => {
                if (data.data.regionCount) {
                    if (data.data.regionCount < 10000) {
                        const results = [
                            {
                                label: regionId,
                                value: `/region/${regionId}?datasetId=${datasetId}`,
                            },
                        ]

                        // If a position is entered, return options for a 40 base region centered
                        // at the position and the position as a one base region.
                        if (start === stop) {
                            const windowRegionId = `${chrom}-${Math.max(
                                1,
                                start - 20
                            )}-${stop + 20}`
                            results.unshift({
                                label: windowRegionId,
                                value: `/region/${windowRegionId}?datasetId=${datasetId}`,
                            })
                        }
                        return results
                    } else {
                        dispatch({ type: TOGGLE_SEARCH_ERROR })
                    }
                }
                return []
            })
    }

    // ==============================================================================================
    // Try search after
    // ==============================================================================================
    
    return apolloClient
        .query<{ searchSuggestions: SearchSuggestions }>({
            query: SEARCH_SUGGESTIONS_QUERY,
            variables: {
                query,
                datasetId
            },
        })
        .then(data => {
            return transformSearchSuggestions(data.data.searchSuggestions, datasetId)
        })
        .then(data => {
            if(data.length <= 0) {
                return matchResults(query, apolloClient, dispatch, datasetId)
            }
            return data
        })
}

const matchResults = (
    query: string,
    apolloClient: ApolloClient<any>,
    dispatch: Dispatch<any>,
    datasetId: string
): Promise<any[]> => {
    // ==============================================================================================
    // Structural Variants
    // ==============================================================================================

    if (isStructuralVariantId(query)) {
        const structuralVariantId = query.toUpperCase()
        return Promise.resolve([
            {
                label: structuralVariantId,
                value: `/variant/${structuralVariantId}?datasetId=${datasetId}`,
            },
        ])
    }
    // ==============================================================================================
    // Variants
    // ==============================================================================================

    if (isVariantId(query)) {
        const variantId = normalizeVariantId(query)
        return Promise.resolve([
            {
                label: variantId,
                value: `/variant/${variantId}?datasetId=${datasetId}`,
            },
        ])
    }

    if (isRsId(query)) {
        const rsId = query
        return Promise.resolve([
            {
                label: rsId,
                value: `/variant/${rsId}?datasetId=${datasetId}`,
            },
        ])
    }

    if (/^CA[0-9]+(\.\d+)?$/i.test(query)) {
        const caid = query.toUpperCase()
        return Promise.resolve([
            {
                label: caid,
                value: `/variant/${caid}?datasetId=${datasetId}`,
            },
        ])
    }

    if (/^[0-9]+(\.\d+)?$/.test(query)) {
        const clinvarVariationId = query
        return Promise.resolve([
            {
                label: clinvarVariationId,
                value: `/variant/${clinvarVariationId}?datasetId=${datasetId}`,
            },
        ])
    }

    // ==============================================================================================
    // Region
    // ==============================================================================================
    /*
    if (isRegionId(query)) {
        const { chrom, start, stop } = parseRegionId(query)
        const regionId = `${chrom}-${start}-${stop}`

        return apolloClient
            .query<{ regionCount: number }>({
                query: REGION_LENGTH_QUERY,
                variables: {
                    chrom,
                    start,
                    stop,
                    datasetId
                },
            })
            .then(data => {
                if (data.data.regionCount) {
                    if (data.data.regionCount < 10000) {
                        const results = [
                            {
                                label: regionId,
                                value: `/region/${regionId}?datasetId=${datasetId}`,
                            },
                        ]

                        // If a position is entered, return options for a 40 base region centered
                        // at the position and the position as a one base region.
                        if (start === stop) {
                            const windowRegionId = `${chrom}-${Math.max(
                                1,
                                start - 20
                            )}-${stop + 20}`
                            results.unshift({
                                label: windowRegionId,
                                value: `/region/${windowRegionId}?datasetId=${datasetId}`,
                            })
                        }
                        return results
                    } else {
                        dispatch({ type: TOGGLE_SEARCH_ERROR })
                    }
                }
                return []
            })
    } */

    // ==============================================================================================
    // Gene ID
    // ==============================================================================================

    const upperCaseQuery = query.toUpperCase()

    if (/^ENSG\d{11}(\.\d+)?$/.test(upperCaseQuery)) {
        const geneId = upperCaseQuery
        return Promise.resolve([
            {
                label: geneId,
                value: `/gene/${geneId}?datasetId=${datasetId}`,
            },
        ])
    }

    // ==============================================================================================
    // Transcript ID
    // ==============================================================================================

    if (/^ENST\d{11}(\.\d+)?$/.test(upperCaseQuery)) {
        const transcriptId = upperCaseQuery
        return Promise.resolve([
            {
                label: transcriptId,
                value: `/transcript/${transcriptId}?datasetId=${datasetId}`,
            },
        ])
    }

    return Promise.resolve([])
}

const transformSearchSuggestions = (suggestions: SearchSuggestions, datasetId: string): any[] => {
    const transformedSuggestions: any[] = []
    if(suggestions) {
        if(suggestions.gene_ids && suggestions.gene_ids.length > 0) {
            suggestions.gene_ids.forEach(geneId => {
                transformedSuggestions.push({
                    value: `/gene/${geneId}?datasetId=${datasetId}`,
                    label: geneId ? geneId : '',
                })
            })
        }

        if(suggestions.gene_symbols && suggestions.gene_symbols.length > 0) {
            suggestions.gene_symbols.forEach(geneSymbol => {
                transformedSuggestions.push({
                    value: `/gene/${geneSymbol}?datasetId=${datasetId}`,
                    label: geneSymbol ? geneSymbol : '',
                })
            })
        }

        if(suggestions.transcript_ids && suggestions.transcript_ids.length > 0) {
            suggestions.transcript_ids.forEach(transcriptId => {
                transformedSuggestions.push({
                    label: transcriptId,
                    value: `/transcript/${transcriptId}?datasetId=${datasetId}`,
                })
            })
        }

        if(suggestions.variant_ids && suggestions.variant_ids.length > 0) {
            suggestions.variant_ids.forEach(variantId => {
                transformedSuggestions.push({
                    label: variantId,
                    value: `/variant/${variantId}?datasetId=${datasetId}`,
                })
            })
        }
    }

    return transformedSuggestions
}
